import { create } from "zustand";
import type { WsPhone, WsQueue, WsTag } from "@/shared/api/types";
import { parseBackendDate } from "@/shared/dashboard/dates";
import type { DashPhone, DashQueueStatus, WsSupervisor } from "./wsTypes";

function tagsStr(p: DashPhone): string {
  if (!p.tags?.length) return "";
  return p.tags.map((t) => t.displayName ?? t.name).join(",");
}

function queuesStr(p: DashPhone): string {
  if (!p.queues?.length) return "";
  return p.queues.map((q) => q.displayName ?? q.queueNumber).join(",");
}

/** gui apiWs.statusBegin: o maior entre sessionStatusBegin e phoneStatusBegin. */
function statusBegin(phone: DashPhone): string {
  if (phone.sessionStatus === "OFFLINE") return "";
  switch (phone.phoneStatus) {
    case "ONLINE":
    case "RINGING":
    case "INUSE":
    case "DIALING": {
      const ssb = String(phone.sessionStatusBegin ?? "");
      const psb = String(phone.phoneStatusBegin ?? "");
      return ssb.toLowerCase().localeCompare(psb.toLowerCase()) >= 0 ? ssb : psb;
    }
    default:
      return "";
  }
}

export function enrichPhone(raw: WsPhone | DashPhone): DashPhone {
  const p = raw as DashPhone;
  const sb = statusBegin(p);
  return {
    ...p,
    statusBegin: sb,
    // gui: computeStatusBeginDate(statusBegin, phoneStatusBegin) — com fallback
    statusBeginDate: parseBackendDate(sb || p.phoneStatusBegin),
    phoneStatusBeginDate: parseBackendDate(p.phoneStatusBegin),
    sessionStatusBeginDate: parseBackendDate(p.sessionStatusBegin),
    tagsStr: tagsStr(p),
    queuesStr: queuesStr(p),
  };
}

function supervisorLabel(s: WsSupervisor): string {
  return typeof s?.fullName === "string" ? s.fullName : String(s?.name ?? "");
}

function sortSupervisors(list: WsSupervisor[]) {
  return [...list].sort((a, b) => supervisorLabel(a).localeCompare(supervisorLabel(b)));
}

function sameSupervisor(a: WsSupervisor, b: WsSupervisor) {
  if (a.id != null && b.id != null) return String(a.id) === String(b.id);
  return supervisorLabel(a) === supervisorLabel(b);
}

export type WsMessage = {
  type: string;
  message?: string;
  phones?: WsPhone[];
  queues?: WsQueue[];
  tags?: WsTag[];
  queuesStatus?: DashQueueStatus[];
  supervisors?: WsSupervisor[];
};

type WsState = {
  statusOnline: boolean;
  statusUpdateConfig: boolean;
  phones: DashPhone[];
  queues: WsQueue[];
  tags: WsTag[];
  queuesStatus: DashQueueStatus[];
  supervisors: WsSupervisor[];
  setStatusOnline: (v: boolean) => void;
  setStatusUpdateConfig: (v: boolean) => void;
  resetRealtime: () => void;
  processPhones: (remove: boolean, phones: (WsPhone | DashPhone)[]) => void;
  processQueues: (remove: boolean, queues: WsQueue[]) => void;
  processTags: (remove: boolean, tags: WsTag[]) => void;
  processQueuesStatus: (remove: boolean, queuesStatus: DashQueueStatus[]) => void;
  processSupervisors: (remove: boolean, supervisors: WsSupervisor[]) => void;
  removePhone: (phoneNumber: string) => void;
  removeTag: (name: string) => void;
  removeQueue: (queueNumber: string) => void;
  removeQueueStatus: (queueName: string) => void;
  removeSupervisor: (id: string) => void;
  handleMessage: (body: WsMessage) => void;
};

export const useWsStore = create<WsState>((set, get) => ({
  statusOnline: false,
  statusUpdateConfig: false,
  phones: [],
  queues: [],
  tags: [],
  queuesStatus: [],
  supervisors: [],

  setStatusOnline: (statusOnline) => set({ statusOnline }),
  setStatusUpdateConfig: (statusUpdateConfig) => set({ statusUpdateConfig }),

  resetRealtime: () =>
    set({
      statusOnline: false,
      statusUpdateConfig: false,
      phones: [],
      queues: [],
      tags: [],
      queuesStatus: [],
      supervisors: [],
    }),

  processPhones: (remove, phones) => {
    let list = remove ? [] : [...get().phones];
    for (const p of phones) {
      if (!remove) list = list.filter((x) => x.phoneNumber !== p.phoneNumber);
      list.push(enrichPhone(p));
    }
    list.sort((a, b) => String(a.phoneNumber).localeCompare(String(b.phoneNumber)));
    set({ phones: list });
  },

  processQueues: (remove, queues) => {
    let list = remove ? [] : [...get().queues];
    for (const q of queues) {
      if (!remove) list = list.filter((x) => x.queueNumber !== q.queueNumber);
      list.push(q);
    }
    list.sort((a, b) => String(a.queueNumber).localeCompare(String(b.queueNumber)));
    set({ queues: list });
  },

  processTags: (remove, tags) => {
    let list = remove ? [] : [...get().tags];
    for (const t of tags) {
      if (!remove) list = list.filter((x) => x.name !== t.name);
      list.push(t);
    }
    list.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    set({ tags: list });
  },

  processQueuesStatus: (remove, queuesStatus) => {
    let list = remove ? [] : [...get().queuesStatus];
    for (const qs of queuesStatus) {
      if (!remove) list = list.filter((x) => x.queueName !== qs.queueName);
      list.push(qs);
    }
    list.sort((a, b) => String(a.queueName).localeCompare(String(b.queueName)));
    set({ queuesStatus: list });
  },

  /** gui apiWs.processSupervisors — REMOVE_AND_UPDATE substitui, UPDATE faz merge. */
  processSupervisors: (remove, supervisors) => {
    let list = remove ? [] : [...get().supervisors];
    for (const s of supervisors) {
      if (!remove) list = list.filter((x) => !sameSupervisor(x, s));
      list.push(s);
    }
    set({ supervisors: sortSupervisors(list) });
  },

  removePhone: (phoneNumber) => set({ phones: get().phones.filter((p) => p.phoneNumber !== phoneNumber) }),

  removeTag: (name) => set({ tags: get().tags.filter((t) => t.name !== name) }),

  removeQueue: (queueNumber) => set({ queues: get().queues.filter((q) => q.queueNumber !== queueNumber) }),

  removeQueueStatus: (queueName) =>
    set({ queuesStatus: get().queuesStatus.filter((q) => q.queueName !== queueName) }),

  removeSupervisor: (id) => set({ supervisors: get().supervisors.filter((s) => String(s.id) !== String(id)) }),

  handleMessage: (body) => {
    const s = get();
    switch (body.type) {
      case "UPDATE_CONFIG":
        s.setStatusUpdateConfig(true);
        break;
      case "EFFECTIVE_CONFIG":
        s.setStatusUpdateConfig(false);
        break;
      case "REMOVE_AND_UPDATE_ALL":
        s.processPhones(true, body.phones ?? []);
        s.processQueues(true, body.queues ?? []);
        s.processTags(true, body.tags ?? []);
        s.processQueuesStatus(true, body.queuesStatus ?? []);
        break;
      case "REMOVE_AND_UPDATE_SUPERVISOR":
        s.processSupervisors(true, body.supervisors ?? []);
        break;
      case "UPDATE_SUPERVISOR":
        s.processSupervisors(false, body.supervisors ?? []);
        break;
      case "REMOVE_SUPERVISOR":
        if (body.message != null) s.removeSupervisor(String(body.message));
        break;
      case "PLAIN_MESSAGE":
        break;
      case "UPDATE_PHONES":
        s.processPhones(false, body.phones ?? []);
        break;
      case "REMOVE_PHONE":
        if (body.message) s.removePhone(body.message);
        break;
      case "UPDATE_TAGS":
        s.processTags(false, body.tags ?? []);
        break;
      case "REMOVE_TAG":
        if (body.message) s.removeTag(body.message);
        break;
      case "UPDATE_QUEUES":
        s.processQueues(false, body.queues ?? []);
        break;
      case "REMOVE_QUEUE":
        if (body.message) s.removeQueue(body.message);
        break;
      case "UPDATE_QUEUES_STATUS":
        s.processQueuesStatus(false, body.queuesStatus ?? []);
        break;
      case "REMOVE_QUEUE_STATUS":
        if (body.message) s.removeQueueStatus(body.message);
        break;
      default:
        break;
    }
  },
}));
